import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Phone, Check, Home as HomeIcon, Truck, Building2, Boxes, Warehouse, Sofa, Trash2, MapPinned, Globe } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SERVICES, FAQS, BUSINESS } from '../data/content';
import QuoteForm from '../components/QuoteForm';
import TrustBar from '../components/TrustBar';
import SEO from '../components/SEO';
import { Reveal } from '../hooks/useReveal';

const ICONS = { Home: HomeIcon, Truck, Building2, Boxes, Warehouse, Sofa, Trash2, MapPinned, Globe };

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find(s => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = ICONS[service.icon] || Truck;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.seoTitle || service.title,
    provider: { '@type': 'MovingCompany', name: 'Laraé Logistics', telephone: BUSINESS.phone },
    areaServed: 'London, Greater London and throughout the United Kingdom',
    description: service.blurb,
  };

  return (
    <>
      <SEO title={`${service.seoTitle || service.title} | Laraé Logistics — Fixed-Price Quotes`}
        description={`${service.short} Fully insured ${service.title.toLowerCase()} across London and throughout the UK. Call ${BUSINESS.phone} for a fixed-price quote.`}
        jsonLd={jsonLd} />

      {/* HERO */}
      <section className="bg-forest text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="w-14 h-14 rounded-2xl bg-oat/20 grid place-items-center text-oat mb-6"><Icon size={26} /></div>
            <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight">{service.title}</h1>
            <p className="font-manrope text-base md:text-lg text-white/80 mt-5 max-w-xl leading-relaxed">{service.blurb}</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/quote" className="bg-oat text-ink font-semibold rounded-full px-7 py-4 inline-flex items-center justify-center gap-2">Get Free Quote <ArrowRight size={18} /></Link>
              <a href={`tel:${BUSINESS.phoneRaw}`} className="bg-white/10 border border-white/30 text-white font-medium rounded-full px-7 py-4 inline-flex items-center justify-center gap-2"><Phone size={18} /> {BUSINESS.phone}</a>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur">
            <h3 className="font-outfit text-lg font-semibold text-white mb-4">What's included</h3>
            <ul className="space-y-3">
              {service.bullets.map(b => (<li key={b} className="flex items-start gap-3"><div className="w-5 h-5 rounded-full bg-oat grid place-items-center shrink-0 mt-0.5"><Check size={12} className="text-forest" /></div><span className="font-manrope text-sm text-white/90">{b}</span></li>))}
            </ul>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* OTHER SERVICES */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <h2 className="font-outfit text-2xl md:text-3xl font-medium text-ink mb-8">Related services</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.filter(s => s.slug !== slug).slice(0, 4).map(s => {
              const I = ICONS[s.icon] || Truck;
              return (
                <Link to={`/services/${s.slug}`} key={s.slug} className="bg-white border border-sand rounded-2xl p-5 hover:border-forest transition-all">
                  <I size={20} className="text-forest mb-3" />
                  <div className="font-outfit text-base font-semibold text-ink">{s.title}</div>
                  <div className="font-manrope text-xs text-ink/60 mt-1">{s.short}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section className="bg-sand/50 py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center max-w-xl mx-auto mb-8">
            <h2 className="font-outfit text-3xl md:text-4xl font-medium text-ink tracking-tight">Get your {service.title.toLowerCase()} quote</h2>
            <p className="font-manrope text-base text-ink/65 mt-3">Fixed-price quote in under 60 seconds.</p>
          </div>
          <QuoteForm sourcePage={`service-${slug}`} />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12">
          <h2 className="font-outfit text-3xl md:text-4xl font-medium text-ink mb-8 text-center">FAQs</h2>
          <Accordion type="single" collapsible>
            {FAQS.slice(0, 6).map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-sand">
                <AccordionTrigger className="font-outfit text-base font-medium text-ink text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="font-manrope text-sm text-ink/70 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
